import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";

type Rodape = { registrar: (el: HTMLElement) => () => void; altura: number };

const RodapeCtx = createContext<Rodape>({ registrar: () => () => {}, altura: 0 });

/**
 * Barras fixas no rodapé (salvar configurações, seleção da produção, faixa de atendimento) avisam
 * que ocupam a parte de baixo da tela; o "? Ajuda" fixo sobe a soma das alturas delas pra não ficar
 * por baixo.
 */
export function ProvedorDoRodape({ children }: { children: ReactNode }) {
  const [elementos, setElementos] = useState<HTMLElement[]>([]);
  const [altura, setAltura] = useState(0);

  const registrar = useCallback((el: HTMLElement) => {
    setElementos((atual) => [...atual, el]);
    return () => setElementos((atual) => atual.filter((e) => e !== el));
  }, []);

  useEffect(() => {
    // 12px de respiro por item: o "Ajuda" fica logo acima, sem encostar.
    const medir = () =>
      setAltura(elementos.reduce((soma, el) => {
        const h = el.getBoundingClientRect().height;
        return h ? soma + h + 12 : soma;
      }, 0));
    medir();
    const obs = new ResizeObserver(medir);
    elementos.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [elementos]);

  return <RodapeCtx.Provider value={{ registrar, altura }}>{children}</RodapeCtx.Provider>;
}

/** Ref pra colocar na barra fixa: enquanto ela estiver montada, o "? Ajuda" fica acima dela. */
export function useOcupaRodape<T extends HTMLElement>() {
  const { registrar } = useContext(RodapeCtx);
  const desfazer = useRef<(() => void) | null>(null);
  return useCallback(
    (el: T | null) => {
      desfazer.current?.();
      desfazer.current = el ? registrar(el) : null;
    },
    [registrar],
  );
}

export const useAlturaDoRodape = () => useContext(RodapeCtx).altura;
